import { EventRound, EventSong, HitsterEvent } from '../types';

export interface RoundCardEntry {
  song: EventSong;
  cardNumber: number;
}

export interface TimelineCardEntry extends RoundCardEntry {
  timelinePosition: number;
  side: 'left' | 'right';
}

export interface TimelineLayout {
  orderedCards: TimelineCardEntry[];
  leftCards: TimelineCardEntry[];
  rightCards: TimelineCardEntry[];
}

export const sortRoundSongs = <T extends Pick<EventSong, 'order'>>(songs: T[]): T[] => {
  return [...songs].sort((left, right) => {
    const leftOrder = left.order ?? Number.MAX_SAFE_INTEGER;
    const rightOrder = right.order ?? Number.MAX_SAFE_INTEGER;

    return leftOrder - rightOrder;
  });
};

export const getRoundCardEntries = (songs: EventSong[]): RoundCardEntry[] => {
  return sortRoundSongs(songs).map((song, index) => ({
    song,
    cardNumber: song.order ?? index + 1,
  }));
};

export const getTimelineLayout = (songs: EventSong[], startYear: number): TimelineLayout => {
  const sortedCards = [...getRoundCardEntries(songs)].sort((left, right) => {
    if ((left.song.year || 0) !== (right.song.year || 0)) {
      return (left.song.year || 0) - (right.song.year || 0);
    }

    return left.cardNumber - right.cardNumber;
  });

  const leftCards = sortedCards
    .filter(card => (card.song.year || 0) <= startYear)
    .map((card, index) => ({
      ...card,
      timelinePosition: index + 1,
      side: 'left' as const,
    }));

  const rightCards = sortedCards
    .filter(card => (card.song.year || 0) > startYear)
    .map((card, index) => ({
      ...card,
      timelinePosition: leftCards.length + index + 1,
      side: 'right' as const,
    }));

  return {
    orderedCards: [...leftCards, ...rightCards],
    leftCards,
    rightCards,
  };
};

export const getTeamStartYearForRound = (
  event: HitsterEvent,
  round: EventRound,
  teamId: string
): number => {
  const roundStartYear = round.startYear || 1990;

  if ((round.startYearMode || 'shared') === 'shared') {
    return roundStartYear;
  }

  const team = (event.teams || []).find(item => item.id === teamId);
  const teamRoundData = team?.roundData?.find(item => item.roundId === round.id);

  return teamRoundData?.startYear || roundStartYear;
};